import { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../helpers/withRouter'
import MapContainer from '../MapContainer'
import { PERSISTENT_MAP_HOST_ID } from '../MapSlot'
import { ICoordinate, IExpeditionType, IServiceInfo, IStop } from '../../models'
import { IStoreState } from '../../store/states'
import {
  resetMapChangedCenterAction,
  resetSingleBusStopAction,
  setIsMapCenterWithinOutlinesAction,
  setIsSearchingCoordsInMapAction,
  setPreviewDestinationStopAction,
  setPreviewOriginStopAction,
  setBusStopAction,
  setPickedDateAction,
  setPickedSeatsAction,
  setSelectMarkerFlagAction,
  setShowSingleStopModalAction,
  setSingleBusStopDirectionAction,
  setUserLocationAction
} from '../../store/actions'
import { urls } from '../../routing'
import {
  NewReservationBusStopsStep,
  NewReservationDateStep,
  NewReservationRouteOptionsStep
} from '../../pages'
import { SeatsModal } from '../../pages/NewReservation/DateStep/SeatsModal'
import { StopSchedulesModal } from '../NewReservation'
import { Application } from '../../config'
import StyledHost from './styles'
import { Button, Page } from '../../components'
import { GoogleMapsService, LiteralsService } from '../../services'
import { appsStyleSelected } from '../../helpers/app'
import { isPointInsideOutlines, loadGoogleMaps } from '../../helpers'
import theme from '../../theme'
import { AnyAction } from 'redux'

const { isIframe } = Application

let hasStartedSession = false

interface IStateProps {
  serviceInfos: IServiceInfo[]
  destinationBusStops: IStop[]
  pickedOriginBusStop?: IStop
  pickedDestinationBusStop?: IStop
  areaChanged: boolean
  mapChangedCenter: ICoordinate | null
  userLocation?: ICoordinate
  pickedSeats: number
  pickedPrmSeats: number
  isRoundTrip: boolean
  isSearchingCoordsInMap: boolean
  isMapCenterWithinOutlines: boolean
}

interface IProps extends IStateProps, DispatchProp<AnyAction>, RouteComponentProps {}

interface IState {
  displaySeatsModal: boolean
  started: boolean
  schedulesStop?: IStop
}

class NewReservationLayout extends PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = {
      displaySeatsModal: false,
      started: !isIframe || hasStartedSession
    }
  }

  private handleStart = () => {
    hasStartedSession = true
    loadGoogleMaps()
    this.setState({ started: true })
  }

  public componentDidUpdate(): void {
    const { mapChangedCenter, dispatch, isSearchingCoordsInMap } = this.props
    if (mapChangedCenter) dispatch(resetMapChangedCenterAction())

    if (this.isDoor2Door !== isSearchingCoordsInMap) {
      dispatch(setIsSearchingCoordsInMapAction(this.isDoor2Door))
    }
  }

  private get isDoor2Door(): boolean {
    const { serviceInfos } = this.props
    if (!serviceInfos?.length) return false

    const selected = serviceInfos.filter(serviceInfo => serviceInfo.selected)
    const scope = selected.length ? selected : serviceInfos

    return scope.some(serviceInfo =>
      serviceInfo.expeditionType === IExpeditionType.Door2Door ||
      serviceInfo.expeditionType === IExpeditionType.HybridDoor2Door ||
      serviceInfo.expeditionType === IExpeditionType.DynamicDoor2Door
    )
  }

  // The pin is only a picker while a stop is still missing: origin first, then
  // destination. Once both are set there is nothing left to place.
  private get isPickingOnMap(): boolean {
    const { pickedOriginBusStop, pickedDestinationBusStop } = this.props
    return this.isBusStopsStep && this.isDoor2Door && (!pickedOriginBusStop || !pickedDestinationBusStop)
  }

  private handleCenterSettled = async (coords: ICoordinate, nearbyStop?: IStop) => {
    const { dispatch, serviceInfos, pickedOriginBusStop } = this.props
    if (!this.isPickingOnMap) return

    // Dropping the pin on a real stop picks it, same as tapping its marker,
    // rather than booking an address a metre away from it.
    if (nearbyStop && nearbyStop !== pickedOriginBusStop) {
      dispatch(setIsMapCenterWithinOutlinesAction(true))
      dispatch(setBusStopAction({ stop: nearbyStop }))
      return
    }

    const isWithinOutlines = isPointInsideOutlines(serviceInfos, coords)
    dispatch(setIsMapCenterWithinOutlinesAction(isWithinOutlines))

    const isOrigin = !pickedOriginBusStop
    if (!isWithinOutlines) {
      dispatch(isOrigin ? setPreviewOriginStopAction(undefined) : setPreviewDestinationStopAction(undefined))
      return
    }

    const place = await GoogleMapsService.getPlaceByCoordinates(coords)
    if (!place) {
      dispatch(setIsMapCenterWithinOutlinesAction(false))
      return
    }

    const stop = {
      id: place.id,
      name: place.name,
      location: place.location,
      isFixedStop: false
    } as unknown as IStop

    dispatch(isOrigin ? setPreviewOriginStopAction(stop) : setPreviewDestinationStopAction(stop))
  }

  private get currentStep(): ReactNode {
    const { location } = this.props
    const BusStopsStepAny = NewReservationBusStopsStep as any
    if (location.pathname.startsWith(urls.newReservationBusStopsStep)) {
      return <BusStopsStepAny onRequestNavigateToDate={ this.openSeatsModalOrNavigate } />
    }
    if (location.pathname.startsWith(urls.newReservationDateStep)) {
      return <NewReservationDateStep />
    }
    if (location.pathname.startsWith(urls.newReservationRouteOptionsStep)) {
      return <NewReservationRouteOptionsStep />
    }
    return null
  }

  public render(): ReactNode {
    const { pickedOriginBusStop, pickedDestinationBusStop, pickedSeats, pickedPrmSeats } = this.props
    const showPrm = pickedOriginBusStop?.serviceInfo?.prmSeatsAvailable
      ?? pickedDestinationBusStop?.serviceInfo?.prmSeatsAvailable
      ?? true

    if (isIframe && !this.state.started) {
      return (
        <Page className={`authenticated ${appsStyleSelected?.style}`}>
          <article style={{
            display: 'flex',
            flex: 1,
            width: '100%',
            height: '100%',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 25,
            boxSizing: 'border-box'
          }}>
            <Button
              title={ LiteralsService.get('startBooking') }
              onPress={ this.handleStart }
              styles={{
                fontSize: 22,
                width: 320,
                height: 120,
                padding: '24px 16px',
                whiteSpace: 'normal',
                lineHeight: 1.2,
                boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40`
              }}
              filled
              upper
            />
          </article>
        </Page>
      )
    }

    return (
      <>
        <StyledHost id={ PERSISTENT_MAP_HOST_ID }>
          <MapContainer { ...this.mapProps } />
        </StyledHost>
        { this.currentStep }
        {
          this.state.schedulesStop &&
          <StopSchedulesModal
            stop={ this.state.schedulesStop }
            onClose={ this.handleCloseSchedules }
            onSelectStop={ this.handleSelectScheduledStop }
          />
        }
        <SeatsModal
          visible={ this.state.displaySeatsModal }
          seats={ pickedSeats }
          prmSeats={ pickedPrmSeats }
          showPrm={ showPrm }
          onSave={ this.handleSaveSeats }
          close={ this.closeSeatsModal }
        />
      </>
    )
  }

  private get isBusStopsStep(): boolean {
    return this.props.location.pathname.startsWith(urls.newReservationBusStopsStep)
  }

  private get mapProps() {
    const {
      serviceInfos,
      destinationBusStops,
      pickedOriginBusStop,
      pickedDestinationBusStop,
      areaChanged,
      mapChangedCenter,
      userLocation
    } = this.props

    if (this.isBusStopsStep) {
      return {
        serviceInfos: serviceInfos ?? [],
        inStop: pickedOriginBusStop,
        outStop: pickedDestinationBusStop,
        outBusStops: destinationBusStops,
        isClusterMap: true,
        controlsEnabled: true,
        showRecenterButton: true,
        zoomLevel: 12,
        userLocation,
        centerChanged: mapChangedCenter,
        areaChanged,
        restrictedOrigins: this.getRestrictedOrigins(),
        showCenterPin: this.isPickingOnMap,
        centerPinIsDestination: !!pickedOriginBusStop,
        centerPinOutsideArea: !this.props.isMapCenterWithinOutlines,
        onCenterSettled: this.handleCenterSettled,
        onMarkerClick: this.onStopClick,
        onShowSchedules: this.handleShowSchedules,
        onInfoWindowShow: this.handleOnInfoWindowShow,
        onSetUserLocation: this.handleOnSetUserLocation
      }
    }

    return {
      serviceInfos: [] as IServiceInfo[],
      inStop: pickedOriginBusStop,
      outStop: pickedDestinationBusStop,
      isClusterMap: false,
      controlsEnabled: false,
      correction: 0.000005,
      zoomLevel: 14
    }
  }

  private getRestrictedOrigins = (): number[] => {
    const { serviceInfos } = this.props
    if (!serviceInfos) return []
    let restrictedOrigins: number[] = []

    serviceInfos.forEach((serviceInfo: IServiceInfo) => {
      serviceInfo.restrictedOrigins && serviceInfo.restrictedOrigins.forEach((restrictedOrigin: any) => {
        if (!restrictedOrigins.some(ro => ro === restrictedOrigin)) restrictedOrigins.push(restrictedOrigin)
      })
    })

    const ro = restrictedOrigins.slice(0)
    ro.forEach((r: number) => {
      serviceInfos.forEach((serviceInfo: IServiceInfo) => {
        if (serviceInfo.stops.some((ss: any) => r === ss.id)) {
          if (!serviceInfo.restrictedOrigins.some((o: any) => r === o)) {
            const index = restrictedOrigins.indexOf(r)
            if (index !== -1) restrictedOrigins.splice(index, 1)
          }
        }
      })
    })

    return restrictedOrigins
  }

  private handleShowSchedules = (stop: IStop) => {
    this.setState({ schedulesStop: stop })
  }

  private handleCloseSchedules = () => {
    this.setState({ schedulesStop: undefined })
  }

  // Picking a departure in the timetable also books the day and time it belongs to.
  private handleSelectScheduledStop = (stop: IStop, date: Date) => {
    this.props.dispatch(setPickedDateAction(date))
    this.onStopClick(stop)
  }

  private onStopClick = (stop: IStop) => {
    const { dispatch, serviceInfos } = this.props
    const stopServices = serviceInfos.filter((service) => service.stops.some((serviceStop) => serviceStop.id === stop.id))
    if (stop.serviceInfo.singleStopReservations) {
      if (stopServices.every((serviceInfo) => serviceInfo.roundTrips)) {
        dispatch(setSingleBusStopDirectionAction(true, stop))
        this.openSeatsModalOrNavigate()
      } else {
        dispatch(setShowSingleStopModalAction(true, stop))
      }
    } else {
      dispatch(setBusStopAction({ stop }))
    }
  }

  private openSeatsModalOrNavigate = () => {
    if (isIframe) {
      this.setState({ displaySeatsModal: true })
    } else {
      this.goToDateStep()
    }
  }

  private handleSaveSeats = (seats: number, prmSeats: number) => {
    this.props.dispatch(setPickedSeatsAction(seats, prmSeats))
    this.setState({ displaySeatsModal: false })
    this.goToDateStep()
  }

  private closeSeatsModal = () => {
    if (this.props.isRoundTrip) this.props.dispatch(resetSingleBusStopAction())
    this.setState({ displaySeatsModal: false })
  }

  private goToDateStep = () => {
    const { history } = this.props
    const name = new URLSearchParams(location.search).get('name') ?? history.location.state?.name
    const lastName = new URLSearchParams(location.search).get('lastName') ?? history.location.state?.lastName
    const email = new URLSearchParams(location.search).get('email') ?? history.location.state?.email
    const townId = new URLSearchParams(location.search).get('townId') ?? history.location.state?.townId

    history.push(urls.newReservationDateStep, {
      userData: { name, lastName, email },
      townId
    })
  }

  private handleOnInfoWindowShow = (opened: boolean) => {
    this.props.dispatch(setSelectMarkerFlagAction(opened))
  }

  private handleOnSetUserLocation = () => {
    this.props.dispatch(setUserLocationAction(undefined))
  }
}

const mapStateToProps = ({ newReservation, configuration }: IStoreState): IStateProps => ({
  serviceInfos: newReservation.serviceInfos,
  destinationBusStops: newReservation.destinationBusStops,
  pickedOriginBusStop: newReservation.pickedOriginBusStop,
  pickedDestinationBusStop: newReservation.pickedDestinationBusStop,
  areaChanged: configuration.areaChanged,
  mapChangedCenter: newReservation.mapChangedCenter,
  userLocation: newReservation.userLocation,
  pickedSeats: newReservation.pickedSeats,
  pickedPrmSeats: newReservation.pickedPrmSeats,
  isRoundTrip: newReservation.isRoundTrip,
  isSearchingCoordsInMap: newReservation.isSearchingCoordsInMap,
  isMapCenterWithinOutlines: newReservation.isMapCenterWithinOutlines
})

export default withRouter(connectRedux(mapStateToProps, NewReservationLayout))
